import { useMutation, useQueryClient } from '@tanstack/react-query';

import BackendService from '../services/backendService';
import { LessonContext } from '../services/backendService/types';
import { readImageNaturalSize } from '../utils/fileUtils';
import { generateUniqueId } from '../utils/general';

export type MaterialAttachment = {
  passageId?: string;
  passageTitle?: string;
  classification?: 'embed' | 'inform';
  fileUploadId: string;
  fileName: string;
  mimetype: string;
  fileId: string;
  unprocessedAttachment?: Omit<MaterialAttachment, 'unprocessedAttachment'>;
  attachmentMethod: 'paste' | 'upload';
};

/**
 * Uploads a file the teacher attached and hands back the attachment the
 * composer renders.
 *
 * `skipDocumentProcessing` is the agent v2 contract: the frontend no longer
 * parses or classifies anything, so the raw file is uploaded and only its
 * `fileUploadId` travels on. The backend opens it once, after the agent has
 * asked what the lesson is for. Without the option we keep the v1 behaviour of
 * running /processDocument up front to produce the markdown the old flow feeds
 * the LLM.
 */
export const useUploadMaterialAttachment = (
  tenantId: string,
  { skipDocumentProcessing = false }: { skipDocumentProcessing?: boolean } = {},
) => {
  const client = useQueryClient();
  const {
    mutate: upload,
    mutateAsync: uploadAsync,
    error,
    isPending: isLoading,
    isSuccess,
    data,
    reset,
  } = useMutation({
    mutationFn: async ({
      file,
      attachmentMethod,
      classifyContext,
    }: {
      file: File;
      attachmentMethod: 'paste' | 'upload';
      classifyContext?: LessonContext;
    }): Promise<{
      materialAttachment: MaterialAttachment;
      classification?: 'embed' | 'inform';
    }> => {
      const fileId = generateUniqueId().toString();

      // Images travel with their pixel dimensions: the browser already holds
      // the bytes, so measuring here saves the backend a download-and-decode.
      // `undefined` for everything else, and the upload proceeds either way.
      const naturalSize = await readImageNaturalSize(file);

      const uploadUrlResponse = await BackendService.instance.getUploadUrlV2({
        pathToFile: `game_files/${fileId}`,
        fileType: 'materialAttachment',
        mimeType: file.type,
        fileName: file.name,
        naturalWidth: naturalSize?.naturalWidth,
        naturalHeight: naturalSize?.naturalHeight,
        tenantId,
      });

      const url = uploadUrlResponse.data.url;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url, true);
        xhr.setRequestHeader('Content-Type', file.type);

        xhr.onreadystatechange = function() {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              resolve(fileId);
            } else {
              reject(xhr.response);
            }
          }
        };
        xhr.onerror = function(response) {
          reject(response);
        };
        xhr.send(file);
      });
      const unprocessedFileUploadResponse = await BackendService.instance.markUploadSuccessfulV2(
        uploadUrlResponse.data.uploadId,
        tenantId,
      );
      const unprocessedFileUpload = unprocessedFileUploadResponse.fileUpload;
      if (skipDocumentProcessing || file.type === 'text/plain') {
        return {
          materialAttachment: {
            attachmentMethod,
            fileUploadId: unprocessedFileUpload.id,
            fileName: file.name,
            mimetype: unprocessedFileUpload.mimeType,
            fileId: unprocessedFileUpload.fileId,
          },
        };
      }
      // anything that isn't a paste-in-text needs to be processed into a markdown file
      const processDocumentResponse = await BackendService.instance.materials.processDocument(
        {
          fileUpload: unprocessedFileUpload,
          classifyContext,
        },
      );
      const processedDocumentFileUpload = processDocumentResponse.fileUpload;

      return {
        classification: processDocumentResponse.classification,
        materialAttachment: {
          attachmentMethod,
          unprocessedAttachment: {
            fileUploadId: unprocessedFileUpload.id,
            fileName: file.name,
            mimetype: unprocessedFileUpload.mimeType,
            fileId: unprocessedFileUpload.fileId,
            attachmentMethod,
          },
          fileUploadId: processedDocumentFileUpload.id,
          fileName: processedDocumentFileUpload.fileId,
          mimetype: processedDocumentFileUpload.mimeType,
          fileId: processedDocumentFileUpload.fileId,
          passageId: processDocumentResponse.passageId,
          passageTitle: processDocumentResponse.passageTitle,
          classification: processDocumentResponse.classification,
        },
      };
    },
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['uploadedDesignerImage'],
      });
    },
  });

  return {
    upload,
    /**
     * The same upload as a promise. `upload`'s per-call `onSuccess`/`onError`
     * live on the hook's single mutation observer, which keeps one set of
     * them — so firing several uploads at once leaves only the last call's
     * callbacks connected, and the earlier ones complete in silence. Anything
     * uploading more than one file at a time wants this instead.
     */
    uploadAsync,
    isUploading: isLoading,
    isSuccess: isSuccess,
    error: error,
    data,
    reset,
  };
};

export type PassageAttachment = MaterialAttachment & { classification: 'embed' };

/**
 * Uploads a passage and hands back the attachment.
 *
 * A `text/plain` file skips /processDocument — the parser refuses that mime
 * type, answering "Failed to find content in parsed file" for a perfectly good
 * .txt. A caller that wants pasted text *tidied* rather than taken at its word
 * sends it as HTML instead (`createHtmlFileFromText`), which the parser reads.
 */
export function useUploadPassageAttachment(tenantId: string) {
  const client = useQueryClient();
  const {
    mutate: upload,
    mutateAsync: uploadAsync,
    error,
    isPending: isLoading,
    isSuccess,
    data,
    reset,
  } = useMutation({
    mutationFn: async ({
      file,
      attachmentMethod,
      classifyContext,
    }: {
      file: File;
      attachmentMethod: 'paste' | 'upload';
      classifyContext?: LessonContext;
    }): Promise<{
      materialAttachment: PassageAttachment;
      classification: 'embed';
    }> => {
      const fileId = generateUniqueId().toString();

      const uploadUrlResponse = await BackendService.instance.getUploadUrlV2({
        pathToFile: `game_files/${fileId}`,
        fileType: 'materialAttachment',
        mimeType: file.type,
        fileName: file.name,
        tenantId,
      });

      const url = uploadUrlResponse.data.url;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url, true);
        xhr.setRequestHeader('Content-Type', file.type);

        xhr.onreadystatechange = function() {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              resolve(fileId);
            } else {
              reject(xhr.response);
            }
          }
        };
        xhr.onerror = function(response) {
          reject(response);
        };
        xhr.send(file);
      });
      const unprocessedFileUploadResponse = await BackendService.instance.markUploadSuccessfulV2(
        uploadUrlResponse.data.uploadId,
        tenantId,
      );
      const unprocessedFileUpload = unprocessedFileUploadResponse.fileUpload;
      if (file.type === 'text/plain') {
        return {
          classification: 'embed',
          materialAttachment: {
            classification: 'embed',
            attachmentMethod,
            fileUploadId: unprocessedFileUpload.id,
            fileName: file.name,
            mimetype: unprocessedFileUpload.mimeType,
            fileId: unprocessedFileUpload.fileId,
          },
        };
      }
      // Everything else is processed into the markdown passage file.
      const processDocumentResponse = await BackendService.instance.materials.processDocument(
        {
          fileUpload: unprocessedFileUpload,
          classifyContext,
          isPassage: true, // this tells BE not to bother classifying the file
        },
      );
      const processedDocumentFileUpload = processDocumentResponse.fileUpload;

      return {
        classification: 'embed',
        materialAttachment: {
          attachmentMethod,
          unprocessedAttachment: {
            fileUploadId: unprocessedFileUpload.id,
            fileName: file.name,
            mimetype: unprocessedFileUpload.mimeType,
            fileId: unprocessedFileUpload.fileId,
            attachmentMethod,
          },
          fileUploadId: processedDocumentFileUpload.id,
          fileName: processedDocumentFileUpload.fileId,
          mimetype: processedDocumentFileUpload.mimeType,
          fileId: processedDocumentFileUpload.fileId,
          passageId: processDocumentResponse.passageId,
          passageTitle: processDocumentResponse.passageTitle,
          classification: 'embed',
        },
      };
    },
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['uploadedDesignerImage'],
      });
    },
  });

  return {
    upload,
    /**
     * The same upload as a promise, for callers that have a step of their own
     * to run once the file has been parsed — reading the markdown back out,
     * say. The hook's single mutation observer keeps only the most recent
     * call's `onSuccess`/`onError`, so anything sequential wants this.
     */
    uploadAsync,
    isUploading: isLoading,
    isSuccess: isSuccess,
    error: error,
    data,
    reset,
  };
}
